import { can, PAY_METHODS, type LedgerEntry, type LedgerKind, type PayMethod, type TherapySession } from '@selfon/domain/browser';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { api, type Bill, type ClientRow, type StaffRow } from '../api';
import { useAuth } from '../auth/AuthContext';
import { principalOf } from '../components/Layout';
import { Alert, Chip, Dialog, Empty, Field, Loading, MonthPicker, PageHeader, useLoad } from '../components/ui';
import { kindLabel, kindsFor, METHOD_LABEL, thisMonthKst, todayKst, won } from '../format';

/* 수납 현황: 회기 일정 + 수납 원장으로 계산한 월 청구·수납. 숫자는 서버의 monthBill 결과와 원장 합계에서만 나온다. */

type RowKind = LedgerKind | 'GENERAL';
const ROW_LABEL: Record<RowKind, string> = { VOUCHER: '바우처결제', SELF_PAY: '본인부담금', EXTRA: '추가부담금', GENERAL: '일반' };
/** 결제 수단별 칸(환불은 음수 원장 합계) */
const METH = [['VOUCHER_TERMINAL', '바우처'], ['CARD', '카드'], ['CASH', '현금'], ['TRANSFER', '계좌이체'], ['REFUND', '환불']] as const;
type MethKey = (typeof METH)[number][0];
type Money = Record<MethKey | 'total', number>;
const emptyM = (): Money => ({ total: 0, VOUCHER_TERMINAL: 0, CARD: 0, CASH: 0, TRANSFER: 0, REFUND: 0 });
const addM = (m: Money, e: LedgerEntry) => { m.total += e.amount; if (e.amount < 0) m.REFUND += -e.amount; else m[e.method] += e.amount; };
const nf = (v: number) => Number(v || 0).toLocaleString('ko-KR');
const dash = (v: number) => (v ? nf(v) : '-');
const pct2 = (a: number, b: number) => (b ? `${(Math.min(1, a / b) * 100).toFixed(2)}%` : '0.00%');
/** "언어치료" → "언어", "교육청지원사업-특수교육" → "교육청지원사업" */
const progType = (p: string) => p.replace(/치료|그룹|평가/g, '').trim() || p;
const vType = (v: string) => v.split(/[-(]/)[0]!;

interface Row extends Bill {
  client?: ClientRow; progs: string[]; vouchers: string[]; teachers: StaffRow[]; isV: boolean;
  byKind: Partial<Record<RowKind, Money>>; total: Money; carry: number; plan: number; unit: number; rate: number;
}
interface Filters { client: string; teacher: string; progMode: 'type' | 'name'; prog: string; vMode: 'type' | 'name'; voucher: string; vYes: boolean; vNo: boolean; done: boolean; undone: boolean }
const NO_FILTER: Filters = { client: '', teacher: '', progMode: 'type', prog: '', vMode: 'type', voucher: '', vYes: true, vNo: true, done: true, undone: true };
const TABS = [['class', '수업별 수납현황'], ['period', '기간별 수납현황'], ['client', '이용자별 금액'], ['program', '프로그램별 금액'], ['voucher', '바우처별 금액']] as const;
type Tab = (typeof TABS)[number][0];

function buildRows(bills: Bill[], sessions: TherapySession[], clients: ClientRow[], staff: StaffRow[], ledger: LedgerEntry[]): Row[] {
  const cMap = new Map(clients.map((x) => [x.id, x]));
  const sMap = new Map(staff.map((x) => [x.userId, x]));
  return bills.map((b) => {
    const client = cMap.get(b.clientId);
    const ses = sessions.filter((s) => s.clientId === b.clientId);
    const vouchers = [...new Set(ses.map((s) => s.voucherProgram).filter(Boolean) as string[])];
    const isV = vouchers.length > 0;
    const byKind: Row['byKind'] = {};
    const total = emptyM();
    for (const e of ledger) {
      if (e.clientId !== b.clientId) continue;
      // 비바우처 이용자는 본인부담·바우처 구분 없이 '일반'으로 묶는다(추가부담금은 따로)
      const k: RowKind = isV ? e.kind : e.kind === 'EXTRA' ? 'EXTRA' : 'GENERAL';
      addM((byKind[k] ??= emptyM()), e);
      addM(total, e);
    }
    const unit = client?.unitPrice ?? 0;
    return {
      ...b, client, progs: [...new Set(ses.map((s) => s.program))], vouchers, isV, byKind, total,
      teachers: [...new Set(ses.map((s) => s.therapistId))].map((id) => sMap.get(id)).filter(Boolean) as StaffRow[],
      carry: ses.filter((s) => s.status === 'CANCELLED' && s.cancelKind === 'CARRYOVER').length * unit,
      plan: ses.length, unit, rate: b.sessionCount ? b.completedCount / b.sessionCount : 0,
    };
  });
}

export function Billing() {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const [month, setMonth] = useState(thisMonthKst());
  const [tab, setTab] = useState<Tab>('class');
  // 이름에 마우스를 올리면 미리보기(peek), 누르면 고정(pinned). 고정 중에는 미리보기가 바꾸지 않는다.
  const [sel, setSel] = useState<{ id: string; pinned: boolean } | null>(null);
  const selectedId = sel?.id ?? null;
  const peekTimer = useRef(0);
  const close = useCallback(() => { window.clearTimeout(peekTimer.current); setSel(null); }, []);
  const peek = (id: string | null) => {
    window.clearTimeout(peekTimer.current);
    if (id === null) { setSel((x) => (x?.pinned ? x : null)); return; }
    peekTimer.current = window.setTimeout(() => setSel((x) => (x?.pinned ? x : { id, pinned: false })), 120);
  };
  const pin = (id: string) => { window.clearTimeout(peekTimer.current); setSel((x) => (x?.pinned && x.id === id ? null : { id, pinned: true })); };
  useEffect(() => {
    if (!sel) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('dialog[open]')) close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel, close]);
  useEffect(() => () => window.clearTimeout(peekTimer.current), []);
  const [notice, setNotice] = useState<string | null>(null);
  const [f, setF] = useState<Filters>(NO_FILTER);
  const [exporting, setExporting] = useState(false);
  const [ex, setEx] = useState({ reason: '', unmasked: false });
  const q = useLoad(async () => {
    const [billing, ss, cl, st, led] = await Promise.all([
      api.billing(centerId, month), api.sessions(centerId, month), api.clients(centerId), api.staff(centerId), api.centerLedger(centerId, month),
    ]);
    return { billing, sessions: ss.sessions, clients: cl.clients, staff: st.staff, ledger: led.entries };
  }, [centerId, month]);
  const d = q.data;
  const rows = useMemo(() => (d ? buildRows(d.billing.bills, d.sessions, d.clients, d.staff, d.ledger) : []), [d]);
  const allProgs = useMemo(() => [...new Set((d?.sessions ?? []).map((s) => s.program))].sort(), [d]);
  const allVouchers = useMemo(() => [...new Set((d?.sessions ?? []).map((s) => s.voucherProgram).filter(Boolean) as string[])].sort(), [d]);

  const view = rows.filter((r) => (!f.client || r.clientId === f.client) && (!f.teacher || r.teachers.some((t) => t.userId === f.teacher))
    && (!f.prog || r.progs.some((x) => (f.progMode === 'type' ? progType(x) : x) === f.prog))
    && (!f.voucher || r.vouchers.some((x) => (f.vMode === 'type' ? vType(x) : x) === f.voucher))
    && ((r.isV && f.vYes) || (!r.isV && f.vNo)) && ((r.fullyPaid && f.done) || (!r.fullyPaid && f.undone)));

  const sum = useMemo(() => {
    const s = { plan: 0, normal: 0, done: 0, gross: 0, disc: 0, req: 0, carry: 0, out: 0, byKind: {} as Row['byKind'], total: emptyM() };
    for (const r of view) {
      s.plan += r.plan; s.normal += r.sessionCount; s.done += r.completedCount; s.gross += r.gross; s.disc += r.discount;
      s.req += r.required; s.carry += r.carry; s.out += Math.max(0, r.outstanding);
      for (const [k, m] of Object.entries(r.byKind) as Array<[RowKind, Money]>) { const t = (s.byKind[k] ??= emptyM()); for (const x in m) t[x as keyof Money] += m[x as keyof Money]; }
      for (const x in r.total) s.total[x as keyof Money] += r.total[x as keyof Money];
    }
    return s;
  }, [view]);

  const selected = rows.find((r) => r.clientId === selectedId) ?? null;
  const progOpts = f.progMode === 'type' ? [...new Set(allProgs.map(progType))] : allProgs;
  const vOpts = f.vMode === 'type' ? [...new Set(allVouchers.map(vType))] : allVouchers;
  const chk = (key: 'vYes' | 'vNo' | 'done' | 'undone', label: string) => (
    <label className="bs-chk"><input type="checkbox" checked={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.checked })} />{label}</label>
  );
  const radio = (key: 'progMode' | 'vMode', val: 'type' | 'name', label: string) => (
    <label className="bs-chk"><input type="radio" name={key} checked={f[key] === val} onChange={() => setF({ ...f, [key]: val, [key === 'progMode' ? 'prog' : 'voucher']: '' })} />{label}</label>
  );

  return (
    <div className="bs">
      <PageHeader title="수납 현황" />
      <div className="bs-tabs" role="tablist" aria-label="현황 보기">
        {TABS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={`bs-tab${tab === k ? ' on' : ''}`} onClick={() => { setTab(k); close(); }}>{l}</button>)}
      </div>

      <div className="bs-filters" role="search" aria-label="수납현황 검색 조건">
        <div className="bs-frow">
          <MonthPicker value={month} onChange={(v) => { setMonth(v); close(); }} />
          <select aria-label="이용자" value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })}>
            <option value="">이용자 전체</option>
            {rows.slice().sort((a, b) => a.name.localeCompare(b.name, 'ko')).map((r) => <option key={r.clientId} value={r.clientId}>{r.name}</option>)}
          </select>
          <select aria-label="선생님" value={f.teacher} onChange={(e) => setF({ ...f, teacher: e.target.value })}>
            <option value="">선생님 전체</option>
            {(d?.staff ?? []).filter((s) => s.active).map((s) => <option key={s.userId} value={s.userId}>{s.name} ({s.title})</option>)}
          </select>
        </div>
        <div className="bs-frow">
          <fieldset className="bs-fs"><legend className="sr-only">프로그램</legend>
            <div className="bs-radios">{radio('progMode', 'type', '프로그램 유형으로 검색')}{radio('progMode', 'name', '프로그램 명으로 검색')}</div>
            <select aria-label="프로그램" value={f.prog} onChange={(e) => setF({ ...f, prog: e.target.value })}>
              <option value="">프로그램 전체</option>{progOpts.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </fieldset>
          <fieldset className="bs-fs"><legend className="sr-only">바우처</legend>
            <div className="bs-radios">{radio('vMode', 'type', '바우처 유형으로 검색')}{radio('vMode', 'name', '바우처 명으로 검색')}</div>
            <select aria-label="바우처" value={f.voucher} onChange={(e) => setF({ ...f, voucher: e.target.value })}>
              <option value="">바우처 전체</option>{vOpts.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </fieldset>
        </div>
        <div className="bs-frow bs-checks">
          <div><span className="bs-lbl">바우처 여부</span>{chk('vYes', '바우처')}{chk('vNo', '일반')}</div>
          <div><span className="bs-lbl">수납 상태</span>{chk('done', '완료')}{chk('undone', '미완료')}</div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setF(NO_FILTER)}>조건 초기화</button>
        </div>
      </div>

      {notice && <Alert kind="ok">{notice}</Alert>}
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !d && <Loading />}
      {d && (
        <div className="kpis">
          <div className="kpi"><span>총금액</span><strong>{won(sum.req)}</strong><small>회기금액 {nf(sum.gross)} · 할인 {nf(sum.disc)}</small></div>
          <div className="kpi"><span>수납액</span><strong>{won(sum.total.total)}</strong><small>바우처 {nf(sum.total.VOUCHER_TERMINAL)} · 카드 {nf(sum.total.CARD)} · 이체 {nf(sum.total.TRANSFER)}</small></div>
          <div className={`kpi ${sum.out > 0 ? 'kpi-warn' : ''}`}><span>미수금</span><strong>{won(sum.out)}</strong><small>수납률 {pct2(sum.total.total, sum.req)}</small></div>
          <div className="kpi"><span>수업진행</span><strong>{pct2(sum.done, sum.normal)}</strong><small>완료 {sum.done} / 정상 {sum.normal}회 · 일정 {sum.plan}회</small></div>
        </div>
      )}
      {d && (
        <div className="bs-tools">
          <span className="muted small">{view.length} / {rows.length}명 표시</span>
          <div className="btn-row">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => { setNotice(null); q.reload(); }}>⟳ 새로고침</button>
            {can(p, 'export:download') && <button type="button" className="btn btn-outline btn-sm" onClick={() => { setEx({ reason: '', unmasked: false }); setExporting(true); }} disabled={!view.length}>⤓ 다운로드</button>}
          </div>
        </div>
      )}

      {d && (view.length === 0 ? <Empty>{rows.length ? '검색 조건에 맞는 이용자가 없습니다. 조건을 바꿔 보세요.' : `${month}에 일정이나 수납 기록이 있는 이용자가 없습니다.`}</Empty>
        : tab === 'class' ? <ClassTable month={month} view={view} sum={sum} selectedId={selectedId} onPeek={peek} onPin={pin} />
          : tab === 'period' ? <PeriodTable view={view} ledger={d.ledger} />
            : tab === 'client' ? <GroupTable view={view} label="이용자" keyOf={(r) => [r.name]} />
              : tab === 'program' ? <GroupTable view={view} label="프로그램" keyOf={(r) => (r.progs.length ? r.progs : ['(일정 없음)'])} />
                : <GroupTable view={view} label="바우처" keyOf={(r) => (r.vouchers.length ? r.vouchers : ['일반(비바우처)'])} />)}

      {selected && <>
        <div className={`bs-drawer${sel?.pinned ? '' : ' bs-peek'}`} role="dialog" aria-label={`${selected.name} 수납 상세${sel?.pinned ? '' : ' 미리보기'}`}>
          {sel?.pinned && <p className="bs-pin-note" role="status">고정됨 · 이름을 다시 클릭하거나 닫기를 누르세요.</p>}
          <LedgerPanel key={selected.clientId + month} bill={selected} voucher={selected.isV} month={month} onChanged={q.reload} onClose={close} />
        </div>
        {sel?.pinned && <button type="button" className="bs-scrim" aria-label="수납 상세 닫기" onClick={close} />}
      </>}

      <Dialog title={`${month} 수납현황 다운로드`} open={exporting} onClose={() => setExporting(false)} submitLabel="다운로드"
        onSubmit={async () => {
          const text = await api.exportBilling(centerId, { month, ...ex, clientIds: view.map((r) => r.clientId) });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
          a.download = `수납현황_${month}.csv`;
          document.body.appendChild(a); a.click(); a.remove();
          window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
          setNotice(`${view.length}명의 수납현황을 내려받았습니다. 아동 정보가 담겨 있으니 보관에 주의하세요.`);
        }}>
        <div className="stack">
          <p className="hint">화면에 보이는 {view.length}명만 내려받습니다.</p>
          <Field id="bs-reason" label="다운로드 사유 (5자 이상)"><input id="bs-reason" required minLength={5} maxLength={200} placeholder="예: 10월 교육청 정산 제출" value={ex.reason} onChange={(e) => setEx({ ...ex, reason: e.target.value })} /></Field>
          {me!.role === 'CENTER_DIRECTOR' && !me!.impersonation && <label className="bs-chk"><input type="checkbox" checked={ex.unmasked} onChange={(e) => setEx({ ...ex, unmasked: e.target.checked })} /> 이름을 가리지 않고 받기(원장님만)</label>}
        </div>
      </Dialog>
    </div>
  );
}

/** 결제 구분(행) × 결제 수단(열) 작은 표 */
function PayGrid({ byKind, total, showTotal, isV }: { byKind: Row['byKind']; total: Money; showTotal: boolean; isV: boolean }) {
  const keys = (['VOUCHER', 'SELF_PAY', 'EXTRA', 'GENERAL'] as const).filter((k) => byKind[k] || (isV && k !== 'GENERAL' && !showTotal));
  const list = keys.length ? keys : [isV ? 'VOUCHER' : 'GENERAL'] as RowKind[];
  const cell = (m: Money, key: MethKey | 'total') => <td key={key} className={`num${key === 'REFUND' && m[key] ? ' bs-neg' : ''}`}>{key === 'total' ? nf(m.total) : dash(m[key])}</td>;
  return (
    <table className="bs-pay">
      <thead><tr><th><span className="sr-only">구분</span></th><th>합계</th>{METH.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
      <tbody>
        {list.map((k) => { const m = byKind[k] ?? emptyM(); return <tr key={k}><th scope="row">{ROW_LABEL[k]}</th>{cell(m, 'total')}{METH.map(([mk]) => cell(m, mk))}</tr>; })}
        {showTotal && <tr className="bs-pay-total"><th scope="row">합계</th>{cell(total, 'total')}{METH.map(([mk]) => cell(total, mk))}</tr>}
      </tbody>
    </table>
  );
}

function payStatus(r: Row) {
  if (r.required === 0) return <span className="muted">-</span>;
  if (r.outstanding <= 0) return <span className="bs-ok">{r.outstanding < 0 ? '초과수납' : '수납완료'}</span>;
  return <span className="bs-due"><b aria-hidden="true">+</b> {nf(r.outstanding)}<span className="sr-only">원 미수</span></span>;
}

/** 표 위 좌우 이동 도구: ← 버튼 · 스크롤 막대(끌기·누르기·방향키) · → 버튼. 표가 넘칠 때만 보인다. */
function ScrollRail({ target }: { target: RefObject<HTMLDivElement | null> }) {
  const [m, setM] = useState({ over: false, left: 0, width: 100 });
  const track = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const update = () => {
      const over = el.scrollWidth > el.clientWidth + 1;
      setM({ over, left: over ? (el.scrollLeft / el.scrollWidth) * 100 : 0, width: over ? (el.clientWidth / el.scrollWidth) * 100 : 100 });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [target]);
  const by = (dir: -1 | 1) => { const el = target.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth' }); };
  const jump = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = target.current, tr = track.current;
    if (!el || !tr) return;
    const r = tr.getBoundingClientRect();
    const place = (x: number) => { el.scrollLeft = ((x - r.left) / r.width) * el.scrollWidth - el.clientWidth / 2; };
    place(e.clientX);
    tr.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => place(ev.clientX);
    const up = () => { tr.removeEventListener('pointermove', move); tr.removeEventListener('pointerup', up); tr.removeEventListener('pointercancel', up); };
    tr.addEventListener('pointermove', move); tr.addEventListener('pointerup', up); tr.addEventListener('pointercancel', up);
  };
  return (
    <div className="bs-rail" hidden={!m.over} role="group" aria-label="표 좌우 이동">
      <button type="button" className="btn btn-outline btn-sm bs-rail-btn" onClick={() => by(-1)} aria-label="표 왼쪽으로 이동">←</button>
      <div ref={track} className="bs-rail-track" role="scrollbar" aria-controls="bs-class-wrap" aria-orientation="horizontal" aria-label="표 가로 위치"
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(m.width >= 100 ? 0 : (m.left / (100 - m.width)) * 100)} tabIndex={0}
        onPointerDown={jump} onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); by(e.key === 'ArrowLeft' ? -1 : 1); } }}>
        <span className="bs-rail-thumb" style={{ left: `${m.left}%`, width: `${m.width}%` }} />
      </div>
      <button type="button" className="btn btn-outline btn-sm bs-rail-btn" onClick={() => by(1)} aria-label="표 오른쪽으로 이동">→</button>
    </div>
  );
}

function ClassTable({ month, view, sum, selectedId, onPeek, onPin }: {
  month: string; view: Row[]; sum: { plan: number; normal: number; done: number; gross: number; disc: number; req: number; carry: number; out: number; byKind: Row['byKind']; total: Money };
  selectedId: string | null; onPeek: (id: string | null) => void; onPin: (id: string) => void;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  // 열 순서: 수업정보(이용자) 다음에 수납상태를 앞으로. 순번은 수업정보 칸 안에 작게.
  return (
    <>
    <ScrollRail target={wrap} />
    <div className="table-wrap bs-wrap" id="bs-class-wrap" ref={wrap} tabIndex={0} aria-label="수업별 수납현황 표, 좌우 스크롤">
      <table className="table bs-table">
        <caption className="sr-only">{month} 수업별 수납현황</caption>
        <thead><tr>{['수업정보', '수납상태', '바우처 여부', '수업진행', '정상회기', '회기금액 및 할인', '총금액', '수납금액', '이월금액', '기타사항'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          <tr className="bs-sum">
            <td className="bs-center"><strong>합 계</strong><div className="muted small">{view.length}명</div></td>
            <td className="bs-center">{sum.out > 0 ? <span className="bs-due">{nf(sum.out)}</span> : <span className="bs-ok">수납완료</span>}<div className="muted small">({pct2(sum.total.total, sum.req)})</div></td>
            <td />
            <td className="bs-center"><div className="muted">{pct2(sum.done, sum.normal)}</div>{sum.done} / {sum.normal}</td>
            <td className="bs-center">{sum.normal}회</td>
            <td className="num">{nf(sum.gross)}<div className="muted small">할인: {nf(sum.disc)}</div></td>
            <td className="num"><strong>{nf(sum.req)}</strong></td>
            <td className="bs-paycell"><PayGrid byKind={sum.byKind} total={sum.total} showTotal isV /></td>
            <td className="num"><div className="muted small">이월</div>{nf(sum.carry)}</td>
            <td />
          </tr>
          {view.map((r, i) => {
            const extra = r.byKind.EXTRA?.total ?? 0;
            return (
              <tr key={r.clientId} className={r.clientId === selectedId ? 'selected' : ''}>
                <td className="bs-info">
                  <span className="muted small">{i + 1}. </span>
                  <button type="button" className="link" onClick={() => onPin(r.clientId)} onMouseEnter={() => onPeek(r.clientId)} onMouseLeave={() => onPeek(null)}
                    aria-label={`${r.name} 수납 상세 열기`} aria-expanded={r.clientId === selectedId}>{r.name}</button>
                  <span className="muted small"> ({r.client?.birthYear ?? '-'})</span>
                  <div className="small">{r.progs.join(', ') || '-'}{r.vouchers.length ? ` - ${r.vouchers.join(', ')}` : ' - 일반'}</div>
                  <div className="muted small">{r.teachers.map((t) => `${t.name} ${t.title}`).join(', ') || '담당 미지정'}</div>
                </td>
                <td className="bs-center">{payStatus(r)}</td>
                <td className="bs-center">{r.isV ? <span className="bs-vmark" title="바우처" aria-label="바우처">✓</span> : <span className="sr-only">일반</span>}</td>
                <td className="bs-center"><div className="muted small">{(r.rate * 100).toFixed(2)}%</div>{r.completedCount} / {r.sessionCount}</td>
                <td className="bs-center">{r.sessionCount}회{r.plan !== r.sessionCount && <div className="muted small">취소 {r.plan - r.sessionCount}</div>}</td>
                <td className="num">{nf(r.unit)}<div className="muted small">할인: {nf(r.discount)}</div></td>
                <td className="num">{nf(r.required)}</td>
                <td className="bs-paycell"><PayGrid byKind={r.byKind} total={r.total} showTotal={false} isV={r.isV} /></td>
                <td className="num"><div className="muted small">이월</div>{nf(r.carry)}</td>
                <td>{extra ? <span className="small">추가부담금 / 총금액 = {r.required ? Math.round((extra / r.required) * 1000) / 10 : 0} %</span>
                  : <span className="muted small">{r.discount ? `할인 ${nf(r.discount)}` : ''}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

/** 이용자·프로그램·바우처별 묶음. 여러 항목에 걸친 아동은 금액을 항목 수로 나눠 배분한다. */
function GroupTable({ view, label, keyOf }: { view: Row[]; label: string; keyOf: (r: Row) => string[] }) {
  const g = new Map<string, { k: string; n: number; ses: number; req: number; paid: number; out: number; v: number; sp: number; ex: number }>();
  for (const r of view) {
    const keys = keyOf(r);
    for (const k of keys) {
      const x = g.get(k) ?? { k, n: 0, ses: 0, req: 0, paid: 0, out: 0, v: 0, sp: 0, ex: 0 };
      const share = 1 / keys.length;
      x.n++; x.ses += r.sessionCount * share; x.req += r.required * share; x.paid += r.total.total * share; x.out += Math.max(0, r.outstanding) * share;
      x.v += r.paid.VOUCHER * share; x.sp += r.paid.SELF_PAY * share; x.ex += r.paid.EXTRA * share;
      g.set(k, x);
    }
  }
  const list = [...g.values()].sort((a, b) => b.req - a.req);
  const R = Math.round;
  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">{label}별 금액</caption>
        <thead><tr><th scope="col">{label}</th>{['이용자', '정상회기', '총금액', '바우처결제', '본인부담금', '추가부담금', '수납합계', '미수', '수납률'].map((h) => <th key={h} scope="col" className="num">{h}</th>)}</tr></thead>
        <tbody>
          {list.map((x) => (
            <tr key={x.k}>
              <td><strong>{x.k}</strong></td><td className="num">{x.n}</td><td className="num">{R(x.ses)}</td><td className="num">{nf(R(x.req))}</td>
              <td className="num">{dash(R(x.v))}</td><td className="num">{dash(R(x.sp))}</td><td className="num">{dash(R(x.ex))}</td>
              <td className="num">{nf(R(x.paid))}</td><td className={`num${x.out > 0 ? ' bs-neg' : ''}`}>{dash(R(x.out))}</td>
              <td className="num">{x.req ? `${(Math.min(1, x.paid / x.req) * 100).toFixed(1)}%` : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      
    </div>
  );
}

/** 결제일별 수납(원장의 결제일 기준) */
function PeriodTable({ view, ledger }: { view: Row[]; ledger: LedgerEntry[] }) {
  const ids = new Set(view.map((r) => r.clientId));
  const days = new Map<string, { day: string; cnt: number; m: Money }>();
  for (const e of ledger) {
    if (!ids.has(e.clientId)) continue;
    const x = days.get(e.paidOn) ?? { day: e.paidOn, cnt: 0, m: emptyM() };
    x.cnt++; addM(x.m, e); days.set(e.paidOn, x);
  }
  const list = [...days.values()].sort((a, b) => a.day.localeCompare(b.day));
  if (!list.length) return <Empty>이 조건에 맞는 수납 기록이 없습니다.</Empty>;
  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">결제일별 수납</caption>
        <thead><tr><th scope="col">결제일</th><th scope="col" className="num">건수</th><th scope="col" className="num">합계</th>{METH.map(([, l]) => <th key={l} scope="col" className="num">{l}</th>)}</tr></thead>
        <tbody>
          {list.map((x) => (
            <tr key={x.day}>
              <td className="nowrap">{x.day} ({'일월화수목금토'[new Date(`${x.day}T00:00:00Z`).getUTCDay()]})</td>
              <td className="num">{x.cnt}</td><td className="num"><strong>{nf(x.m.total)}</strong></td>
              {METH.map(([k]) => <td key={k} className={`num${k === 'REFUND' && x.m[k] ? ' bs-neg' : ''}`}>{dash(x.m[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LedgerPanel({ bill, voucher, month, onChanged, onClose }: { bill: Bill; voucher: boolean; month: string; onChanged: () => void; onClose: () => void }) {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const { data, error, loading, reload } = useLoad(() => api.ledger(centerId, bill.clientId, month), [centerId, bill.clientId, month]);
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<LedgerEntry | null>(null);
  const [form, setForm] = useState({ kind: 'SELF_PAY' as LedgerKind, method: 'CARD' as PayMethod, amount: '', paidOn: todayKst() });
  const [reason, setReason] = useState('');
  const refresh = () => { reload(); onChanged(); };
  const entries = data?.entries ?? [];
  const reversed = new Set(entries.filter((e) => e.reversesId).map((e) => e.reversesId));

  return (
    <aside className="panel" aria-labelledby="ledger-title">
      <div className="panel-head">
        <h2 id="ledger-title">{bill.name} · {month} 수납</h2>
        <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="수납 상세 닫기">닫기</button>
      </div>
      <dl className="facts">
        <div><dt>청구</dt><dd>{won(bill.required)}</dd></div>
        <div><dt>수납</dt><dd>{won(bill.paid.total)}</dd></div>
        <div><dt>미수납</dt><dd>{won(Math.max(0, bill.outstanding))}</dd></div>
      </dl>
      {error && <Alert>{error}</Alert>}
      {loading && !data && <Loading />}
      <ul className="ledger">
        {entries.map((e) => (
          <li key={e.id} className={e.amount < 0 ? 'neg' : reversed.has(e.id) ? 'struck' : ''}>
            <div>
              <strong>{kindLabel(e.kind, voucher)}{e.amount < 0 ? ' 취소' : ''}{e.program ? ` · ${e.program}` : ''}</strong>
              <span className="muted"> · {METHOD_LABEL[e.method]} · {e.paidOn}</span>
              {e.reason && <p className="muted small">사유: {e.reason}</p>}
            </div>
            <div className="ledger-right">
              <span className="num">{won(e.amount)}</span>
              {e.amount > 0 && !reversed.has(e.id) && can(p, 'ledger:reverse') && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setReason(''); setReversing(e); }}>환불</button>
              )}
              {reversed.has(e.id) && <Chip tone="muted">취소됨</Chip>}
            </div>
          </li>
        ))}
        {data && entries.length === 0 && <li className="muted">이달 수납 기록이 없습니다.</li>}
      </ul>
      {can(p, 'ledger:write') && <button type="button" className="btn btn-primary" onClick={() => { setForm({ ...form, amount: '' }); setAdding(true); }}>수납 등록</button>}
      {!can(p, 'ledger:write') && <p className="hint">수납 등록·환불은 원장님 권한입니다.</p>}

      <Dialog title={`${bill.name} 수납 등록`} open={adding} onClose={() => setAdding(false)} submitLabel="등록"
        onSubmit={async () => { await api.addPayment(centerId, bill.clientId, { billingMonth: month, kind: form.kind, method: form.method, amount: Number(form.amount), paidOn: form.paidOn }); refresh(); }}>
        <Field id="kind" label="수납 구분">
          <select id="kind" value={form.kind} onChange={(e) => { const kind = e.target.value as LedgerKind; setForm({ ...form, kind, method: kind === 'VOUCHER' ? 'VOUCHER_TERMINAL' : form.method === 'VOUCHER_TERMINAL' ? 'CARD' : form.method }); }}>
            {kindsFor(voucher).map((k) => <option key={k} value={k}>{kindLabel(k, voucher)}</option>)}
          </select>
        </Field>
        <Field id="method" label="결제 수단">
          <select id="method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as PayMethod })}>
            {PAY_METHODS.filter((m) => (form.kind === 'VOUCHER') === (m === 'VOUCHER_TERMINAL')).map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
          </select>
        </Field>
        <Field id="amount" label="금액(원)">
          <input id="amount" type="number" inputMode="numeric" min={1} step={1} required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} aria-describedby="amount-hint" />
        </Field>
        <Field id="paidOn" label="결제일"><input id="paidOn" type="date" max="9999-12-31" required value={form.paidOn} onChange={(e) => setForm({ ...form, paidOn: e.target.value })} /></Field>
      </Dialog>

      <Dialog title="환불(결제 취소)" danger open={Boolean(reversing)} onClose={() => setReversing(null)} submitLabel="환불 처리"
        onSubmit={async () => { await api.reversePayment(centerId, reversing!.id, { reason, refundedOn: todayKst() }); refresh(); }}>
        {reversing && <p>{kindLabel(reversing.kind, voucher)} {won(reversing.amount)} ({reversing.paidOn}) 건을 취소합니다. 원래 기록은 지워지지 않고, 같은 금액의 취소 기록이 추가됩니다.</p>}
        <Field id="reason" label="환불 사유"><input id="reason" required minLength={2} maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </Dialog>
    </aside>
  );
}
